import React, { useEffect, useMemo } from "react"
import { useState } from "react"
import Select from "react-select"
import { Form, Modal } from "react-bootstrap"
import { Button } from "react-bootstrap"
import DatePicker from "react-datepicker"
import { useAddVesselToList } from "../hooks/useAddVesselToList"
import { useFetchVesselsByList } from "../hooks/useFetchVesselsByList"
import { VesselTrackingList } from "../hooks/useFetchList"
import { VesselInfo } from "../hooks/useFetchVesselInfo"
import { toast } from "react-toastify"
import VesselTrackingVesselSearch from "./VesselTrackingVesselSearch"
import { addDays } from "date-fns"
import { getThreeMonthsFromNow } from "../helpers/helper"
import { useDeleteVesselFromList } from "../hooks/useDeleteVesselFromList"
import { isAxiosError } from "axios"
import { useSelector } from "react-redux"
import { RootState } from "store"
import { usePrevetGetVessels } from "modules/Prevetting/hooks/usePrevetGetVessels"
import { VesselSegment } from "user/userService"

const POSTPANAMAX = "postpanamax"

const SEGMENT_DWT_RANGES: Record<string, { min: number; max: number }> = {
  [VesselSegment.Handysize]: { min: 10000, max: 39900 },
  [VesselSegment.Supramax]: { min: 40000, max: 67900 },
  [VesselSegment.Panamax]: { min: 68000, max: 99900 },
  [POSTPANAMAX]: { min: 85000, max: 120000 },
  [VesselSegment.Capesize]: { min: 100000, max: 499900 },
}

// "postpanamax" is an artificial label spanning the Panamax/Capesize boundary —
// when selected, it should match vessels in either underlying segment.
const SEGMENT_FILTER_EXPANSIONS: Record<string, string[]> = {
  [POSTPANAMAX]: [VesselSegment.Panamax, VesselSegment.Capesize],
}

const formatSegmentLabel = (s: string): string =>
  s === POSTPANAMAX ? "PostPanamax" : s.charAt(0).toUpperCase() + s.slice(1)

const dwtToKt = (v: number | null): string =>
  v === null ? "" : String(v / 1000)

const ktInputToDwt = (input: string): number | null => {
  if (input === "") return null
  const n = Number(input)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 1000)
}

const parseIntInput = (input: string): number | null => {
  if (input === "") return null
  const n = Number(input)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

const currentYear = new Date().getFullYear()

interface VesselTrackingAddVesselModalProps {
  showAddVesselModal: boolean
  closeAddVesselModal: () => void
  selectedListId: string
  fetchVesselInfo: (listID: string[]) => Promise<void>
  trackingList: VesselTrackingList[]
}

function VesselTrackingAddVesselModal({
  showAddVesselModal,
  closeAddVesselModal,
  selectedListId,
  fetchVesselInfo,
  trackingList,
}: VesselTrackingAddVesselModalProps) {
  const { BulkAddVesselToList, isBulkAddVesselLoading } = useAddVesselToList()
  const { vesselInfoByList, getVesselsByList, isGetVesselsByListsLoading } =
    useFetchVesselsByList()
  const [selectedList, setSelectedList] = useState<string>(selectedListId)
  const [vesselInfos, setVesselInfos] = useState<VesselInfo[] | undefined>([])
  const [selectedImos, setSelectedImos] = useState<string[]>([])
  const [expirationDate, setExpirationDate] = useState<Date>(
    getThreeMonthsFromNow()
  )

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [deleteVesselid, setDeleteVesselId] = useState<string | undefined>("")
  const [isMultiFilter, setIsMultiFilter] = useState<boolean>(false)

  const { deleteVesselFromList, isDeleteVesselLoading } =
    useDeleteVesselFromList()

  const user = useSelector((state: RootState) => state.user)
  // append postpanamax
  const vesselSegments: (VesselSegment | string)[] | undefined =
    user.chosenOrganization?.segments
  const { vesselsData, getVesselsLoading } = usePrevetGetVessels({
    vesselSegments,
  })

  const [segmentFilter, setSegmentFilter] = useState<string | null>(null)
  const [dwtMin, setDwtMin] = useState<number | null>(null)
  const [dwtMax, setDwtMax] = useState<number | null>(null)
  const [ageFrom, setAgeFrom] = useState<number | null>(null)
  const [ageTo, setAgeTo] = useState<number | null>(null)
  const [designFilter, setDesignFilter] = useState<string | null>(null)
  const [isBulkLoading, setIsBulkLoading] = useState<boolean>(false)

  const builtMin = ageTo !== null ? currentYear - ageTo : null
  const builtMax = ageFrom !== null ? currentYear - ageFrom : null

  const segmentFilterValues = useMemo<string[] | null>(
    () =>
      segmentFilter === null
        ? null
        : SEGMENT_FILTER_EXPANSIONS[segmentFilter] ?? [segmentFilter],
    [segmentFilter]
  )

  const activeImos = useMemo(
    () => vesselInfos?.map((vessel) => vessel.vesselImo) || [],
    [vesselInfos]
  )

  const SEGMENT_ORDER: (VesselSegment | string)[] = [
    VesselSegment.Handysize,
    VesselSegment.Supramax,
    VesselSegment.Panamax,
    "postpanamax",
    VesselSegment.Capesize,
  ]
  const isArtificialSegment = (s: string): boolean =>
    s in SEGMENT_FILTER_EXPANSIONS
  const availableSegments: string[] =
    vesselSegments && vesselSegments.length > 0
      ? [
          ...SEGMENT_ORDER.filter(
            (s) => isArtificialSegment(s) || vesselSegments.includes(s)
          ),
          ...vesselSegments.filter((s) => !SEGMENT_ORDER.includes(s)),
        ]
      : SEGMENT_ORDER

  const uniqueDesigns = useMemo(() => {
    if (!vesselsData) return []
    const values = new Set<string>()
    for (const v of vesselsData) {
      if (v.design) values.add(v.design)
      if (v.designer) values.add(v.designer)
    }
    return [...values].sort()
  }, [vesselsData])

  //fetch vessels by list when modal opens
  useEffect(() => {
    if (showAddVesselModal && selectedListId) {
      const listIds = trackingList.map((list) => list.id)
      if (listIds.length > 0) {
        getVesselsByList(listIds)
      }
    }
    const date = getThreeMonthsFromNow()
    setExpirationDate(date)
  }, [showAddVesselModal])

  //set activeList from vesseltrackinginfo component
  useEffect(() => {
    if (selectedListId) {
      setSelectedList(selectedListId)
    }
  }, [selectedListId])

  //set vesselinfos
  useEffect(() => {
    if (selectedList && vesselInfoByList) {
      setVesselInfos(
        vesselInfoByList.find((list) => list.listID === selectedList)
          ?.vesselInfo
      )
    }
  }, [selectedList, vesselInfoByList])

  //check if user uses multifilters
  useEffect(() => {
    const activeFilters = [
      segmentFilter !== null,
      segmentFilter !== "all",
      dwtMin !== null || dwtMax !== null,
      ageFrom !== null || ageTo !== null,
      designFilter !== null,
    ].filter(Boolean).length

    if (
      activeFilters > 1 &&
      ageFrom === null &&
      ageTo === null &&
      designFilter === null
    ) {
      setIsMultiFilter(false)
    }

    setIsMultiFilter(activeFilters > 1)
  }, [segmentFilter, dwtMin, dwtMax, ageFrom, ageTo, designFilter])

  const handleAddVesselsToList = async () => {
    if (selectedImos.length === 0) return
    setIsBulkLoading(true)

    const vessels = selectedImos
      .map((imo) => vesselsData?.find((vd) => String(vd.value) === String(imo)))
      .filter((v): v is NonNullable<typeof v> => v !== undefined)

    if (vessels.length === 0) {
      setIsBulkLoading(false)
      return
    }

    try {
      await BulkAddVesselToList({
        listId: selectedList,
        vesselImo: vessels.map((v) => String(v.value)),
        vesselName: vessels.map((v) => v.label.split("(")[0].trim()),
        vesselDwt: vessels.map((v) => v.dwt?.toString() || "0"),
        vesselSegment: vessels.map((v) => v.segment),
        expirationDate: expirationDate,
      })
      toast.success(
        `Added ${vessels.length} vessel${
          vessels.length === 1 ? "" : "s"
        } to list`,
        { position: "bottom-right", autoClose: 8000 }
      )
    } catch (err) {
      console.error("bulk add failed", err)
      toast.error("Failed to add vessels", {
        position: "bottom-right",
        autoClose: 8000,
      })
    } finally {
      setIsBulkLoading(false)
      setSelectedImos([])
      await getVesselsByList(trackingList.map((list) => list.id))
      if (selectedList === selectedListId) {
        await fetchVesselInfo([selectedListId])
      }
    }
  }

  const handleDeleteVesselFromList = async (vesselID: string | undefined) => {
    try {
      if (vesselID) {
        await deleteVesselFromList(vesselID)
        const message = "Vessel removed from favourites"
        toast.success(message, {
          position: "bottom-right",
          autoClose: 8000,
        })
      } else {
        toast.warn("Vessel ID error", {
          position: "bottom-right",
          autoClose: 8000,
        })
      }
    } catch (err: unknown) {
      const message = "Failed to delete vessel from list"
      console.error(message, err)
      if (isAxiosError(err)) {
        toast.error(message, {
          position: "bottom-right",
          autoClose: 8000,
        })
      }
    } finally {
      await fetchVesselInfo([selectedList])
      await getVesselsByList([selectedList])
      setShowDeleteModal(false)
    }
  }

  return (
    <>
      <Modal show={showAddVesselModal} onHide={closeAddVesselModal} size="xl">
        <Modal.Header
          closeButton
          className="d-flex flex-row gap-2 align-items-center"
          style={{ flexWrap: "nowrap" }}
        >
          <h5 className="text-nowrap mb-0">Add Vessel[s]</h5>
          <Form.Select
            onChange={(e) => setSelectedList(e.target.value)}
            value={selectedList}
            style={{ maxWidth: 150 }}
          >
            {isGetVesselsByListsLoading ? (
              <option disabled>Loading...</option>
            ) : (
              vesselInfoByList.map((list) => {
                return (
                  <option key={list.listID} value={list.listID}>
                    {
                      trackingList.find((tkList) => tkList.id === list.listID)
                        ?.listName
                    }
                  </option>
                )
              })
            )}
          </Form.Select>
          <VesselTrackingVesselSearch
            activeImos={activeImos}
            selectedImos={selectedImos}
            setSelectedImos={setSelectedImos}
            vesselsData={vesselsData}
            getVesselsLoading={getVesselsLoading}
            segmentFilter={segmentFilterValues}
            dwtMin={dwtMin}
            dwtMax={dwtMax}
            builtMin={builtMin}
            builtMax={builtMax}
            designFilter={designFilter}
            isMultiFilter={isMultiFilter}
          />
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              border: "1px solid #ced4da",
              borderRadius: "0.375rem",
              background: "#fff",
              width: 250,
              overflow: "hidden",
            }}
          >
            <Form.Select
              style={{
                flex: "0 0 140px",
                margin: 0,
                border: 0,
                borderRight: "1px solid #ced4da",
                borderRadius: 0,
                boxShadow: "none",
              }}
              value={segmentFilter ?? "all"}
              onChange={(e) => {
                const value = e.target.value
                if (value === "all") {
                  setSegmentFilter(null)
                  setDwtMin(null)
                  setDwtMax(null)
                } else {
                  setSegmentFilter(value)
                  const range = SEGMENT_DWT_RANGES[value]
                  if (range) {
                    setDwtMin(range.min)
                    setDwtMax(range.max)
                  }
                }
              }}
            >
              <option value="all">Segments</option>
              {availableSegments.map((seg) => (
                <option key={seg} value={seg}>
                  {formatSegmentLabel(seg)}
                </option>
              ))}
            </Form.Select>
            <input
              type="text"
              inputMode="decimal"
              placeholder="min"
              value={dwtToKt(dwtMin)}
              onChange={(e) => setDwtMin(ktInputToDwt(e.target.value))}
              aria-label="DWT min (kt)"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                padding: "0.375rem 0 0.375rem 0.5rem",
                textAlign: "center",
              }}
            />
            <span
              style={{
                alignSelf: "center",
                color: "#6c757d",
                padding: "0 4px",
                userSelect: "none",
              }}
            >
              -
            </span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="max"
              value={dwtToKt(dwtMax)}
              onChange={(e) => setDwtMax(ktInputToDwt(e.target.value))}
              aria-label="DWT max (kt)"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                padding: "0.375rem 0",
                textAlign: "center",
              }}
            />
            <span
              style={{
                alignSelf: "center",
                color: "#6c757d",
                padding: "0 0.5rem 0 4px",
                userSelect: "none",
              }}
            >
              {dwtMin != null || dwtMax != null ? "kt" : ""}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              border: "1px solid #ced4da",
              borderRadius: "0.375rem",
              background: "#fff",
              width: 150,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                alignSelf: "center",
                color: "#6c757d",
                padding: "0 0.5rem 0 0.75rem",
                userSelect: "none",
              }}
            >
              Age
            </span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="from"
              value={ageFrom !== null ? String(ageFrom) : ""}
              onChange={(e) => setAgeFrom(parseIntInput(e.target.value))}
              aria-label="Age from (years)"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                padding: "0.375rem 0",
                textAlign: "center",
              }}
            />
            <span
              style={{
                alignSelf: "center",
                color: "#6c757d",
                padding: "0 4px",
                userSelect: "none",
              }}
            >
              -
            </span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="to"
              value={ageTo !== null ? String(ageTo) : ""}
              onChange={(e) => setAgeTo(parseIntInput(e.target.value))}
              aria-label="Age to (years)"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                padding: "0.375rem 0",
                textAlign: "center",
              }}
            />
            <span
              style={{
                alignSelf: "center",
                color: "#6c757d",
                padding: "0 0.5rem 0 4px",
                userSelect: "none",
              }}
            >
              {ageFrom != null || ageTo != null ? "yrs" : ""}
            </span>
          </div>
          <Select
            isClearable
            placeholder="Design/er"
            options={uniqueDesigns.map((d) => ({ value: d, label: d }))}
            value={
              designFilter
                ? {
                    value: designFilter,
                    label: designFilter,
                  }
                : null
            }
            onChange={(opt) => setDesignFilter(opt ? opt.value : null)}
            styles={{
              control: (base) => ({ ...base, minWidth: 130, width: 130 }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
            menuPortalTarget={document.body}
          />
          <div style={{ width: 150 }}>
            <DatePicker
              selected={expirationDate}
              placeholderText="Expiration Date"
              onChange={(e) => {
                if (e) setExpirationDate(e)
              }}
              showIcon
              icon={"ti-calendar"}
              className="h-50 w-100"
              minDate={addDays(new Date(), 1)}
              dateFormat={"dd/MM/yyyy"}
            />
          </div>
          <Button
            variant="primary"
            disabled={
              isGetVesselsByListsLoading ||
              isBulkAddVesselLoading ||
              isBulkLoading ||
              selectedImos.length === 0 ||
              !expirationDate
            }
            onClick={handleAddVesselsToList}
            className="px-3 text-nowrap"
            title={
              selectedImos.length > 0
                ? `Add ${selectedImos.length} selected vessel${
                    selectedImos.length === 1 ? "" : "s"
                  }`
                : "Select vessels to add"
            }
          >
            {isBulkAddVesselLoading || isBulkLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              `+${selectedImos.length > 0 ? ` (${selectedImos.length})` : ""}`
            )}
          </Button>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "50vh", overflow: "auto" }}>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th style={{ fontWeight: "bold" }}>IMO</th>
                <th style={{ fontWeight: "bold" }}>Vessel Name</th>
                <th style={{ fontWeight: "bold" }}>DWT</th>
                <th style={{ fontWeight: "bold" }}>Segment</th>
                <th style={{ fontWeight: "bold" }}>Expiration Date</th>
                <th style={{ fontWeight: "bold" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isGetVesselsByListsLoading ? (
                <tr>
                  <td colSpan={6} className="text-muted text-center py-2">
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  </td>
                </tr>
              ) : vesselInfos && vesselInfos.length > 0 ? (
                vesselInfos.map((vessel) => {
                  return (
                    <tr key={vessel.id}>
                      <td>{vessel.vesselImo}</td>
                      <td>{vessel.vesselName}</td>
                      <td>{vessel.vesselDwt}</td>
                      <td>{vessel.vesselSegment}</td>
                      <td>
                        <div className="bg-black text-white py-1 ps-2 border-radius-1 rounded">
                          {new Date(vessel.expirationDate)
                            .toLocaleDateString("en-US", {
                              year: "numeric",
                              day: "numeric",
                              month: "short",
                            })
                            .toUpperCase()}
                        </div>
                      </td>
                      <td
                        style={{
                          cursor: selectedListId.includes("all")
                            ? "not-allowed"
                            : "pointer",

                          border: "none",
                        }}
                      >
                        <button
                          className="btn ms-1 text-danger border-0 align-items-center"
                          onClick={() => {
                            setShowDeleteModal(true)
                            setDeleteVesselId(vessel.id)
                          }}
                          disabled={selectedListId.includes("all")}
                        >
                          <span className="ti-trash fs-5" />
                        </button>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-muted text-center py-2">
                    No vessels in this list
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Modal.Body>
      </Modal>

      {/* Modal for delete */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Vessel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this vessel from your list of
          favourites?
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleteVesselLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => handleDeleteVesselFromList(deleteVesselid)}
            disabled={isDeleteVesselLoading}
          >
            {isDeleteVesselLoading ? "Deleting..." : "Delete Vessel"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default VesselTrackingAddVesselModal
